import { ref } from 'vue';
import { QR_RTM_COMMAND, RTM_CONNECT_TIMEOUT_MS, RTM_OPERATION_TIMEOUT_MS } from '../../constants/qr';

const isString = value => typeof value === 'string' && value.trim().length > 0;
const LINK_STATES = ['IDLE', 'CONNECTING', 'CONNECTED', 'DISCONNECTED', 'SUSPENDED', 'FAILED'];
const LINK_REASONS = ['CONNECTION_FAILURE', 'LOGIN_FAILURE', 'LOGIN_TIMEOUT', 'INTERRUPTED', 'LOGOUT', 'BANNED_BY_SERVER', 'TOKEN_EXPIRED', 'TOKEN_WILL_EXPIRE', 'NETWORK_ERROR', 'UNKNOWN'];

// eslint-disable-next-line no-unused-vars
const logEvent = (event, details) => {
    // Debug logging placeholder - enable via DEBUG_AGORA env var
    // This is a stub for future analytics tracking
};

// The SDK throws DUPLICATE_USER_ID for a second instance with the same appId+userId for the page lifetime
const sharedClients = new Map();
export const clearRtmClientCache = () => sharedClients.clear();

function validConfig(config) {
    return config && (config.channelType === undefined || config.channelType === 'MESSAGE') &&
        ['appId', 'userId', 'token', 'channelName', 'messageChannelName', 'callId'].every(key => isString(config[key])) &&
        (config.useStringUserId === undefined || typeof config.useStringUserId === 'boolean');
}

function bounded(operation, timeout, waits = new Set()) {
    return new Promise((resolve, reject) => {
        const complete = (callback, value) => {
            clearTimeout(timer);
            waits.delete(cancel);
            callback(value);
        };
        const cancel = () => complete(reject, new Error('cancelled'));
        const timer = setTimeout(() => complete(reject, new Error('timeout')), timeout);
        waits.add(cancel);
        Promise.resolve().then(operation).then(value => complete(resolve, value), error => complete(reject, error));
    });
}

// Native clients publish JSONEncoder Data, which the Web SDK delivers as BINARY
function messageText(event) {
    if (event.messageType === 'STRING') return typeof event.message === 'string' ? event.message : null;
    if (event.messageType !== 'BINARY') return null;
    const bytes = event.message;
    if (!ArrayBuffer.isView(bytes) && Object.prototype.toString.call(bytes) !== '[object ArrayBuffer]') return null;
    try {
        return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
        return null;
    }
}

function incomingCommand(event, config) {
    if (event.channelType !== 'MESSAGE' || event.channelName !== config.messageChannelName ||
        !isString(event.publisher) || event.publisher === config.userId) return null;
    const text = messageText(event);
    if (text === null) return null;
    let message;
    try {
        message = JSON.parse(text);
    } catch {
        return null;
    }
    if (!message || Array.isArray(message) ||
        ![QR_RTM_COMMAND.DIRECT_REJECT, QR_RTM_COMMAND.CALL_ENDED, QR_RTM_COMMAND.CALL_EXPIRE].includes(message.callType)) return null;
    if (message.channelName !== config.channelName ||
        (message.callId != null && !isString(message.callId))) return null;
    if (message.callId !== config.callId) return null;
    return message.callType;
}

export function useAgoraRtm() {
    const connected = ref(false);
    const errorCode = ref(null);
    let session = null;
    let generation = 0;
    const isCurrent = current => session === current && current.active;
    const ownsClient = current => {
        const shared = sharedClients.get(current.clientKey);
        return shared?.client === current.client && shared.owner === current;
    };
    function cleanupStaleOperation(current, cleanup) {
        const shared = sharedClients.get(current.clientKey);
        if (shared?.client !== current.client || shared.owner !== current) return Promise.resolve();
        const release = shared.release;
        const operation = Promise.resolve(release).catch(() => {}).then(async () => {
            if (ownsClient(current)) await cleanup();
        });
        shared.release = operation;
        return operation;
    }

    function close(current) {
        if (!current) return Promise.resolve();
        if (current.cleanup) return current.cleanup;
        logEvent('disconnect: starting');
        current.active = false;
        current.waits.forEach(cancel => cancel());
        current.listeners.forEach(([name, handler]) => current.client.removeEventListener(name, handler));
        current.cleanup = (async () => {
            if (current.client) {
                const results = await Promise.allSettled([
                    bounded(() => current.client.unsubscribe(current.config.messageChannelName), RTM_OPERATION_TIMEOUT_MS),
                    bounded(() => current.client.logout(), RTM_OPERATION_TIMEOUT_MS),
                ]);
                logEvent('disconnect: completed', {
                    unsubscribed: results[0].status === 'fulfilled', loggedOut: results[1].status === 'fulfilled',
                });
            } else {
                logEvent('disconnect: completed', { clientCreated: false });
            }
        })();
        const shared = sharedClients.get(current.clientKey);
        if (current.client && shared?.client === current.client && shared.owner === current) {
            shared.release = current.cleanup;
        }
        return current.cleanup;
    }

    function disconnect() {
        generation++;
        const current = session;
        session = null;
        connected.value = false;
        return close(current);
    }

    async function connect(resolveConfig, handlers = {}) {
        const previous = session;
        const version = ++generation;
        session = null;
        connected.value = false;
        errorCode.value = null;
        await close(previous);
        if (version !== generation) return false;
        logEvent('connect: starting');
        const current = { active: true, waits: new Set(), listeners: [], client: null, config: null, renewal: null };
        session = current;
        const deadline = Date.now() + RTM_CONNECT_TIMEOUT_MS;
        const setup = operation => bounded(operation, Math.max(1, deadline - Date.now()), current.waits);
        const listen = (name, handler) => {
            current.listeners.push([name, handler]);
            current.client.addEventListener(name, handler);
        };
        let failure = 'connect_failed';
        try {
            const supplied = await setup(() => typeof resolveConfig === 'function' ? resolveConfig() : resolveConfig);
            if (!isCurrent(current)) return false;
            failure = 'invalid_config';
            if (!validConfig(supplied)) throw new Error('Invalid RTM configuration');
            current.config = { ...supplied, channelType: 'MESSAGE' };
            failure = 'connect_failed';
            const { default: AgoraRTM } = await setup(() => import('agora-rtm-sdk'));
            if (!isCurrent(current)) return false;
            const clientKey = `${current.config.appId}-${current.config.userId}`;
            const shared = sharedClients.get(clientKey);
            if (shared) {
                let release;
                do {
                    release = shared.release;
                    await setup(() => release);
                    if (!isCurrent(current)) return false;
                } while (shared.release !== release);
            }
            logEvent('client: initializing', {
                channelType: current.config.channelType,
                useStringUserId: current.config.useStringUserId !== false,
                reused: Boolean(shared),
            });
            current.client = shared?.client || (current.config.useStringUserId === undefined
                ? new AgoraRTM.RTM(current.config.appId, current.config.userId)
                : new AgoraRTM.RTM(current.config.appId, current.config.userId, { useStringUserId: current.config.useStringUserId }));
            current.clientKey = clientKey;
            sharedClients.set(clientKey, { client: current.client, release: Promise.resolve(), owner: current });
            listen('message', event => {
                if (!isCurrent(current)) return;
                // TEMP debug: full payload incl. callId/channel; remove before release
                logEvent('message: raw', {
                    messageType: event.messageType, channelType: event.channelType, channelName: event.channelName,
                    publisher: event.publisher, rawKind: event.message?.constructor?.name ?? typeof event.message,
                    message: messageText(event) ?? event.message,
                });
                const command = incomingCommand(event, current.config);
                const text = messageText(event);
                let payload = null;
                try {
                    payload = text === null ? null : JSON.parse(text);
                } catch {
                    payload = null;
                }
                logEvent('message', {
                    command, accepted: command !== null, messageType: event.messageType,
                    channelMatches: event.channelType === 'MESSAGE' && event.channelName === current.config.messageChannelName,
                    selfPublished: event.publisher === current.config.userId,
                    stringMessage: event.messageType === 'STRING', messageChannel: event.channelType === 'MESSAGE',
                    parsed: payload !== null && typeof payload === 'object',
                    callType: Object.values(QR_RTM_COMMAND).includes(payload?.callType) ? payload.callType : null,
                    payloadChannelMatches: payload?.channelName === current.config.channelName,
                    callIdPresent: payload?.callId != null, callIdMatches: payload?.callId === current.config.callId,
                });
                if (command) handlers.onCommand?.(command);
            });
            const subscribe = () => {
                if (!isCurrent(current)) return Promise.resolve();
                logEvent('subscribe: starting');
                return current.client.subscribe(current.config.messageChannelName, {
                    withMessage: true, withPresence: false, withMetadata: false, withLock: false,
                }).then(async result => {
                    if (!isCurrent(current)) {
                        await cleanupStaleOperation(current, async () => {
                            await bounded(
                                () => current.client.unsubscribe(current.config.messageChannelName),
                                RTM_OPERATION_TIMEOUT_MS
                            ).catch(() => {});
                            if (ownsClient(current)) {
                                await bounded(() => current.client.logout(), RTM_OPERATION_TIMEOUT_MS).catch(() => {});
                            }
                        });
                    } else {
                        logEvent('subscribe: succeeded');
                    }
                    return result;
                });
            };
            const login = () => {
                if (!isCurrent(current)) return Promise.resolve();
                logEvent('login: starting');
                return current.client.login({ token: current.config.token }).then(async result => {
                    if (!isCurrent(current)) {
                        await cleanupStaleOperation(current, () =>
                            bounded(() => current.client.logout(), RTM_OPERATION_TIMEOUT_MS).catch(() => {}));
                    } else logEvent('login: succeeded');
                    return result;
                });
            };
            const renew = expired => {
                if (!isCurrent(current)) return Promise.resolve();
                if (typeof current.config.renewToken !== 'function') {
                    if (expired) {
                        errorCode.value = 'token_expired';
                        handlers.onTokenExpired?.();
                    }
                    return Promise.resolve();
                }
                if (current.renewal) return current.renewal;
                logEvent('renewal: starting', { expired });
                const operation = (async () => {
                    try {
                        if (typeof current.config.renewToken !== 'function') throw new Error('Renewal unavailable');
                        const token = await bounded(() => current.config.renewToken(), RTM_CONNECT_TIMEOUT_MS, current.waits);
                        if (!isCurrent(current)) return;
                        if (!isString(token)) throw new Error('Invalid token');
                        current.config.token = token;
                        if (expired) {
                            current.ready = false;
                            await bounded(() => current.client.logout(), RTM_OPERATION_TIMEOUT_MS, current.waits);
                            if (!isCurrent(current)) return;
                            current.listeners.forEach(([name, handler]) => {
                                current.client.removeEventListener(name, handler);
                                current.client.addEventListener(name, handler);
                            });
                            await bounded(login, RTM_CONNECT_TIMEOUT_MS, current.waits);
                            if (!isCurrent(current)) return;
                            await bounded(subscribe, RTM_CONNECT_TIMEOUT_MS, current.waits);
                            if (!isCurrent(current)) return;
                            current.ready = true;
                            connected.value = true;
                            handlers.onRestored?.();
                        } else {
                            await bounded(() => current.client.renewToken(token), RTM_CONNECT_TIMEOUT_MS, current.waits);
                        }
                        if (isCurrent(current)) {
                            errorCode.value = null;
                            logEvent('renewal: succeeded', { expired });
                        }
                    } catch {
                        if (isCurrent(current)) {
                            errorCode.value = 'renewal_failed';
                            if (expired) handlers.onTokenExpired?.();
                            logEvent('renewal: failed', { errorCode: 'renewal_failed' });
                        }
                    }
                })();
                current.renewal = operation;
                operation.finally(() => {
                    if (current.renewal === operation) current.renewal = null;
                });
                return operation;
            };
            listen('tokenPrivilegeWillExpire', channelName => {
                if (!isCurrent(current)) return;
                logEvent('tokenPrivilegeWillExpire', { loginToken: !channelName });
                if (!channelName) return renew(false);
            });
            listen('linkState', event => {
                if (!isCurrent(current)) return;
                logEvent('linkState', {
                    currentState: LINK_STATES.includes(event.currentState) ? event.currentState : 'UNKNOWN',
                    previousState: LINK_STATES.includes(event.previousState) ? event.previousState : 'UNKNOWN',
                    reasonCode: LINK_REASONS.includes(event.reasonCode) ? event.reasonCode : 'UNKNOWN',
                    subscriptionUnrestored: Boolean(event.unrestoredChannels?.includes(current.config.messageChannelName)),
                });
                const wasConnected = connected.value;
                connected.value = event.currentState === 'CONNECTED';
                if (event.currentState === 'FAILED') {
                    errorCode.value = 'connection_failed';
                    if (event.reasonCode === 'TOKEN_EXPIRED') return renew(true);
                }
                if (!connected.value || wasConnected || !current.ready) return;
                if (event.unrestoredChannels?.includes(current.config.messageChannelName)) {
                    return bounded(subscribe, RTM_CONNECT_TIMEOUT_MS, current.waits).then(() => {
                        if (isCurrent(current)) handlers.onRestored?.();
                    }).catch(() => {
                        if (isCurrent(current)) {
                            errorCode.value = 'subscription_failed';
                            logEvent('subscribe: failed', { errorCode: 'subscription_failed' });
                        }
                    });
                }
                handlers.onRestored?.();
            });
            await setup(login);
            if (!isCurrent(current)) return false;
            await setup(subscribe);
            if (!isCurrent(current)) return false;
            current.ready = true;
            connected.value = true;
            logEvent('transport: ready', { channelType: current.config.channelType });
            logEvent('connect: succeeded');
            return true;
        } catch (error) {
            if (!isCurrent(current)) return false;
            session = null;
            connected.value = false;
            errorCode.value = failure;
            logEvent('connect: failed', {
                errorCode: failure,
                ...(Number.isFinite(error?.code) ? { sdkCode: error.code } : {}),
            });
            await close(current);
            return false;
        }
    }

    async function sendCallEnded() {
        const current = session;
        if (!current?.ready || !isCurrent(current)) {
            logEvent('publish: skipped', { command: QR_RTM_COMMAND.CALL_ENDED, reason: 'session_unavailable' });
            return false;
        }
        const config = current.config;
        const message = { callType: QR_RTM_COMMAND.CALL_ENDED, channelName: config.channelName, callId: config.callId };
        logEvent('publish: starting', { command: QR_RTM_COMMAND.CALL_ENDED });
        try {
            await bounded(() => current.client.publish(config.messageChannelName, JSON.stringify(message), {
                channelType: 'MESSAGE',
            }), RTM_OPERATION_TIMEOUT_MS, current.waits);
            if (isCurrent(current)) logEvent('publish: succeeded', { command: QR_RTM_COMMAND.CALL_ENDED });
            return isCurrent(current);
        } catch {
            if (isCurrent(current)) {
                errorCode.value = 'publish_failed';
                logEvent('publish: failed', { command: QR_RTM_COMMAND.CALL_ENDED, errorCode: 'publish_failed' });
            }
            return false;
        }
    }

    return { connected, errorCode, connect, disconnect, sendCallEnded };
}